import { ReferenceDirection } from '@ardoq/api-types';
import { getId } from '../getId';
import { TraversalBuilderState } from '../types';
import { addNodeAndEdge } from './addNodeAndEdge';
import { ToggleTripleOptionPayload } from './editTraversalActions';
import { getReferenceIdsBetweenStartAndSelectedContext } from './getReferenceIdsBetweenStartAndSelectedContext';
import { namedDirectedTripleToBackendFormat } from './namedDirectedTripleToBackendFormat';
import { pathToKey } from './pathToKey';
import { rebuildSelectedGraphAndAccordingState } from './rebuildSelectedGraph';
import { removeEdgesForTriples } from './removeEdges';

export const toggleTripleOption = (
  state: TraversalBuilderState,
  { tripleId, direction, namedDirectedTriple }: ToggleTripleOptionPayload
): TraversalBuilderState => {
  const { tripleOptions, selectedGraphNodeId } = state;
  const graphNodes = new Map(state.graphNodes);
  const graphEdges = new Map(state.graphEdges);
  const referenceMap = new Map(state.selectedGraph.referenceMap);
  const instanceCounts = new Map(state.instanceCounts);
  if (!selectedGraphNodeId) {
    throw Error('Option selected with no selected graph node context');
  }
  const isOutgoing = direction === ReferenceDirection.OUTGOING;

  const option = (
    isOutgoing ? tripleOptions.outgoing : tripleOptions.incoming
  ).find(({ tripleId: tripleIdCandidate }) => tripleIdCandidate === tripleId);
  if (!option) {
    throw Error('Missing option in handleToggleOption');
  }

  const referenceIdsToStartContext = new Set(
    getReferenceIdsBetweenStartAndSelectedContext({
      ...state.selectedGraph,
      currentContextId: state.startNode!.id,
      targetContextId: selectedGraphNodeId,
    })
  );

  if (option.isSelected) {
    removeEdgesForTriples({
      graphNodeId: selectedGraphNodeId,
      // TODO fix this
      tripleIds: [tripleId],
      direction,
      selectedGraph: state.selectedGraph,
      referenceMap,
      referenceIdsToStartContext,
      excludeReferenceIds: new Set(
        state.pathCollapsing.rules.flatMap(({ virtualEdgeStates }) =>
          virtualEdgeStates.flatMap(({ collapsedEdges }) => collapsedEdges)
        )
      ),
    });
  } else {
    const { instanceCounts: instanceCountsOption } = option;
    const { graphNode } = addNodeAndEdge({
      graphNodeId: selectedGraphNodeId,
      tripleId,
      direction,
      state,
      graphNodes,
      graphEdges,
      referenceMap,
      getId,
      instanceCountsOption,
      shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings:
        state.shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings,
    });
    const instanceCount = instanceCounts.get(selectedGraphNodeId);
    if (instanceCount) {
      const namedDirectedTriplePath = [
        ...instanceCount.namedDirectedTriplePath,
        namedDirectedTripleToBackendFormat(namedDirectedTriple),
      ];
      instanceCounts.set(graphNode.id, {
        namedDirectedTriplePath,
        pathKey: pathToKey(namedDirectedTriplePath),
        label: graphNode.getLabel(),
        graphNodeId: graphNode.id,
        counts:
          instanceCountsOption === null ? undefined : instanceCountsOption,
      });
    }
  }

  return rebuildSelectedGraphAndAccordingState({
    graphNodes,
    graphEdges,
    state: {
      ...state,
      isInitialPath: false,
    },
    referenceMap,
    instanceCounts,
  });
};
